import { ArrowLeft, ArrowRight } from "lucide-react";

import { Button } from "@/components/button";

interface PaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
}

const PAGE_BUTTON_CLASS = "min-w-[36px] rounded-none -ml-[1px]";
const HIDE_BELOW_MEDIUM = "hidden @2xl:inline-flex";
const HIDE_BELOW_WIDE = "hidden @4xl:inline-flex";

function generatePaginationRange(
  currentPage: number,
  totalPages: number,
): ("..." | number)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
}

export function Pagination({
  currentPage,
  onPageChange,
  totalPages,
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const paginationRange = generatePaginationRange(currentPage, totalPages);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;
  const isSibling = (pageNumber: number) =>
    totalPages > 7 &&
    pageNumber !== 1 &&
    pageNumber !== totalPages &&
    pageNumber !== currentPage;

  return (
    <div className="flex items-center">
      <Button
        aria-label="Previous page"
        className="flex items-center gap-1 rounded-md @2xl:rounded-l-md @2xl:rounded-r-none"
        color="gray"
        isDisabled={isFirstPage}
        onPress={() => !isFirstPage && onPageChange(currentPage - 1)}
        size="sm"
        variant="secondary"
      >
        <ArrowLeft size={14} />
        <span className="hidden @4xl:inline">Previous</span>
      </Button>
      <span className="px-3 text-sm text-text-tertiary @2xl:hidden">
        {currentPage} / {totalPages}
      </span>
      {paginationRange.map((pageNumber, index) => {
        if (pageNumber === "...") {
          return (
            <Button
              className={`${PAGE_BUTTON_CLASS} ${HIDE_BELOW_MEDIUM} pointer-events-none`}
              color="gray"
              key={`elipsis` + index}
              onPress={() => {}}
              size="sm"
              variant="secondary"
            >
              ...
            </Button>
          );
        }

        const isCurrentPage = pageNumber === currentPage;

        return (
          <Button
            className={`${PAGE_BUTTON_CLASS} ${
              isSibling(pageNumber) ? HIDE_BELOW_WIDE : HIDE_BELOW_MEDIUM
            } ${isCurrentPage ? "bg-[rgb(var(--button-tertiary-bg-hover))]" : ""}`}
            color="gray"
            key={pageNumber}
            onPress={() => onPageChange(pageNumber)}
            size="sm"
            variant="secondary"
          >
            {pageNumber}
          </Button>
        );
      })}
      <Button
        aria-label="Next page"
        className="flex items-center gap-1 rounded-md @2xl:rounded-r-md @2xl:rounded-l-none @2xl:-ml-[1px]"
        color="gray"
        isDisabled={isLastPage}
        onPress={() => !isLastPage && onPageChange(currentPage + 1)}
        size="sm"
        variant="secondary"
      >
        <span className="hidden @4xl:inline">Next</span>
        <ArrowRight size={14} />
      </Button>
    </div>
  );
}
